import { BffApiClientMolecule } from "@/app/api-client/store"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { Flow } from "@/instrumentation/logger/types"
import type { OpenSeaOfferRef } from "@anaconda/core/bid/types"
import type { NftMiniature } from "@anaconda/core/nft/types"
import type { OpenSeaOfferFulfillment } from "@anaconda/core/opensea/offer-fulfillment"
import { Trans } from "@lingui/react/macro"
import {
  type Address,
  type Blockchain,
  acceptOpenSeaOfferFactoryReatom,
  approveOpenSeaConduitFactoryReatom,
} from "@rarible_int/anaconda_sdk/ethereum"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import { molecule } from "bunshi"
import { EvmSdkContainerMoleculeReatom } from "../../blockchains/evm-sdk-container-molecule.reatom"
import { AppToast, SuccessViewTransaction } from "../../toasts/app-toast"
import {
  trackTransactionCompleted,
  trackTransactionFailed,
  trackTransactionStarted,
} from "../events/track-transaction"
import { getTransactionErrorMessage } from "../events/transaction-event"
import { invalidateBoughtNftQueries } from "../order/invalidate-bought-nft-queries"
import {
  invalidateNftOffersQueries,
  removeFilledOpenSeaOffer,
} from "../order/invalidate-nft-offers-queries"

export interface EvmAcceptOpenSeaOfferParams {
  nft: NftMiniature
  offer: OpenSeaOfferRef
  blockchain: Blockchain
}

/**
 * Selling into an OpenSea offer: OpenSea composes a Seaport
 * `matchAdvancedOrders`, the BFF and the SDK both check it, and the seller
 * sends it straight to Seaport.
 *
 * The fulfilment carries a SignedZone signature that lapses within minutes, so
 * it is re-fetched after any approval rather than reused across one. Fetching
 * first also tells us which conduit the sale pulls the NFT through, so the
 * approval never targets a guessed one.
 */
export const EvmAcceptOpenSeaOfferMoleculeReatom = molecule((mol) => {
  const logger = mol(LoggerMolecule)
  const bffApiClient = mol(BffApiClientMolecule)
  const { sdkContainerAtom } = mol(EvmSdkContainerMoleculeReatom)
  const { evmAddress: evmAddressAtom } = mol(WalletConnectMoleculeReatom)

  const acceptOpenSeaOfferActionFactory = () => {
    const acceptAction = action(
      async ({ nft, offer, blockchain }: EvmAcceptOpenSeaOfferParams) => {
        const from = evmAddressAtom() as Address | undefined
        const container = sdkContainerAtom()

        if (!from) {
          throw new Error("Wallet address is not available to accept an offer")
        }

        const fetchFulfillment = async () => {
          const response = await wrap(
            bffApiClient.api.nfts.opensea["offer-fulfillment"].$post({
              json: {
                itemId: nft.id,
                orderHash: offer.orderHash,
                protocolAddress: offer.protocolAddress,
                fulfiller: from,
              },
            }),
          )

          if (response.status === 409) {
            throw new Error("This OpenSea offer is no longer available")
          }
          if (response.status === 503) {
            throw new Error("OpenSea offers are temporarily unavailable")
          }
          if (!response.ok) {
            throw new Error("OpenSea could not prepare this sale")
          }

          const { fulfillment } = await wrap(response.json())
          return fulfillment as OpenSeaOfferFulfillment
        }

        // The SDK validates the fulfilment against the offer on this row before
        // it approves anything or sends anything — never against the order hash
        // the response carries about itself.
        const sale = {
          from,
          blockchain,
          itemId: nft.id,
          orderHash: offer.orderHash,
        }

        let fulfillment = await wrap(fetchFulfillment())

        const approval = await wrap(
          approveOpenSeaConduitFactoryReatom(container)({
            ...sale,
            fulfillment,
          }),
        )
        if (approval) fulfillment = await wrap(fetchFulfillment())

        const { txId } = await wrap(
          acceptOpenSeaOfferFactoryReatom(container)({ ...sale, fulfillment }),
        )

        return { txId, nft, blockchain, orderHash: offer.orderHash }
      },
      "evmAcceptOpenSeaOfferAction",
    ).extend(
      withAsync(),
      withCallHook((_, [{ nft }]) => {
        sonnerToast.custom(
          (id) => (
            <AppToast.Pending
              id={id}
              title="Accept offer"
              main={<Trans>Accept OpenSea offer</Trans>}
              second={<Trans>Pending...</Trans>}
            />
          ),
          { duration: Number.POSITIVE_INFINITY },
        )
        trackTransactionStarted({ logger, flow: Flow.AcceptOffer, nft })
      }),
    )

    acceptAction.onReject.extend(
      withCallHook(({ error, params: [{ nft }] }) => {
        console.error("Accept OpenSea offer failed:", error)
        trackTransactionFailed({ logger, flow: Flow.AcceptOffer, nft, error })

        const message = getTransactionErrorMessage(error)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Error
            id={id}
            title="Accept offer"
            main={<Trans>Accepting the offer failed</Trans>}
            second={
              <div className="font-semibold text-destructive">{message}</div>
            }
          />
        ))
      }),
    )

    acceptAction.onFulfill.extend(
      withCallHook(({ payload }) => {
        const { txId, nft, blockchain, orderHash } = payload
        removeFilledOpenSeaOffer(nft.id, orderHash)
        // Refetch once OpenSea has had time to drop the filled order; an
        // immediate refetch could still see it listed.
        setTimeout(() => invalidateNftOffersQueries(nft.id), 15_000)
        invalidateBoughtNftQueries(nft)
        trackTransactionCompleted({
          logger,
          flow: Flow.AcceptOffer,
          nft,
          txId,
        })

        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Success
            id={id}
            title="Accept offer"
            main={<Trans>Offer accepted</Trans>}
            second={
              <SuccessViewTransaction
                transactionId={txId}
                blockchain={blockchain}
              />
            }
          />
        ))
      }),
    )

    return acceptAction
  }

  return { acceptOpenSeaOfferActionFactory }
})
